import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useCurrentLanguage from '../../hooks/useCurrentLanguage';
import { usePxToVw } from '../../hooks/usePxToVw';
import LanguageBox from './LanguageBox';

const LanguageSelector = ({ userId, onLanguageChange, currentLanguage: controlledLanguage }) => {
  const { pxToVw } = usePxToVw();
  const {
    currentLanguage: hookLanguage,
    updateLanguage,
    loading,
    getAvailableLanguages,
  } = useCurrentLanguage(userId);

  const currentLanguage = controlledLanguage !== undefined ? controlledLanguage : hookLanguage;

  // Fetch customization settings from backend
  const [customization, setCustomization] = useState({
    outerBackgroundColor: "#808080",
    outerOpacity: 100,
    outerBorderRadius: 15,
    outerBorderWidth: 2,
    outerBorderColor: "#ffffff",
    outerPadding: 15,
    gap: 10,
    innerBackgroundColor: "#ffffff",
    innerOpacity: 20,
    innerBorderRadius: 9,
    innerBorderWidth: 2,
    innerBorderColor: "#ffffff",
    innerPadding: 15,
    fontFamily: "inherit",
    fontSize: 18,
    fontWeight: "700",
    textColor: "#ffffff",
    enableIcon: true,
    flagSize: 60,
  });

  const [languageIcons, setLanguageIcons] = useState({}); // Store custom icons per language
  const [languageIconSizes, setLanguageIconSizes] = useState({}); // Store custom icon sizes per language

  const [offsets, setOffsets] = useState({
    left: 20,
    top: 50,
    right: null,
  });

  useEffect(() => {
    if (userId) {
      fetchCustomization();
      fetchOffsets();
      fetchLanguageSettings();
    }
  }, [userId]);

  const fetchCustomization = async () => {
    try {
      const response = await axios.get(`/api/language/customization/${userId}`);
      if (response.data.success && response.data.customization) {
        setCustomization(prev => ({ ...prev, ...response.data.customization }));
      }
    } catch (error) {
      console.error('Error fetching language customization:', error);
    }
  };

  const fetchLanguageSettings = async () => {
    try {
      const response = await axios.get(`/api/language/settings/${userId}`);
      if (response.data.success && response.data.languages) {
        // Extract custom icons and sizes from language settings
        const icons = {};
        const sizes = {};
        response.data.languages.forEach(lang => {
          if (lang.customIconUrl) {
            icons[lang.code] = lang.customIconUrl;
          }
          if (lang.customIconSize) {
            sizes[lang.code] = lang.customIconSize;
          }
        });
        setLanguageIcons(icons);
        setLanguageIconSizes(sizes);
      }
    } catch (error) {
      console.error('Error fetching language settings:', error);
    }
  };

  const fetchOffsets = async () => {
    try {
      const response = await axios.get(`/api/language/offsets/${userId}`);
      if (response.data.success && response.data.offsets) {
        setOffsets(response.data.offsets);
      }
    } catch (error) {
      console.error('Error fetching language offsets:', error);
    }
  };

  const handleLanguageChange = (newLanguage) => {
    updateLanguage(newLanguage);
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
  };

  const c = customization;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: pxToVw(10),
        padding: pxToVw(12),
        color: '#666',
      }}>
        <div style={{
          width: pxToVw(20),
          height: pxToVw(20),
          border: `${pxToVw(2)} solid #f3f3f3`,
          borderTop: `${pxToVw(2)} solid #007bff`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}></div>
        <span>Loading...</span>
      </div>
    );
  }

  const languages = getAvailableLanguages();

  // Convert hex color to rgba with opacity
  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/css/flag-icons.min.css"
      />
      <div
        style={{
          position: 'fixed',
          left: `${offsets.left}%`,
          top: `${offsets.top}%`,
          transform: 'translate(-50%, -50%)',
          backgroundColor: hexToRgba(c.outerBackgroundColor, c.outerOpacity),
          padding: pxToVw(c.outerPadding),
          borderRadius: pxToVw(c.outerBorderRadius),
          border: `${pxToVw(c.outerBorderWidth)} solid ${c.outerBorderColor}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToVw(c.gap),
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
      >
        {languages.map((lang) => (
          <LanguageBox
            key={lang.code}
            language={lang}
            customization={c}
            isSelected={lang.code === currentLanguage}
            onClick={() => handleLanguageChange(lang.code)}
            size="normal"
            customIconUrl={languageIcons[lang.code]}
            customIconSize={languageIconSizes[lang.code]}
          />
        ))}
      </div>
    </>
  );
};

export default LanguageSelector;
